import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchPackages, postEstimate, postSimulate } from "@/lib/api";
import { useSimulation } from "@/context/SimulationContext";
import { fmtRp, fmtNum } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Battery, Sun, TrendingUp } from "lucide-react";

export default function RencanaTumbuh() {
  const { input, result, selectedTier } = useSimulation();
  const [packages, setPackages] = useState([]);
  const [tier, setTier] = useState(selectedTier || "Safer");
  const [addPanels, setAddPanels] = useState(2);
  const [addBatt, setAddBatt] = useState(1);
  const [current, setCurrent] = useState(null);
  const [scenarios, setScenarios] = useState({});

  useEffect(() => { fetchPackages().then((d) => setPackages(d.packages || [])); }, []);

  useEffect(() => {
    if (!packages.length) return;
    const va = input.daya_pln_lain || input.daya_pln_va || 3500;
    const pkg = packages.find((p) => p.va === va && p.tier === tier) || packages.find((p) => p.tier === tier);
    if (!pkg) return;
    (async () => {
      const base = await postEstimate({ package_id: pkg.id });
      setCurrent({ pkg, estimate: base });
      const [addP, addB, addBoth] = await Promise.all([
        postEstimate({ package_id: pkg.id, panel_count_override: pkg.panels + addPanels, battery_count_override: pkg.battery_units }),
        postEstimate({ package_id: pkg.id, panel_count_override: pkg.panels, battery_count_override: pkg.battery_units + addBatt }),
        postEstimate({ package_id: pkg.id, panel_count_override: pkg.panels + addPanels, battery_count_override: pkg.battery_units + addBatt }),
      ]);
      setScenarios({ addP, addB, addBoth });
    })();
  }, [packages, tier, addPanels, addBatt, input]);

  const Delta = ({ label, baseVal, newVal, unit = "" }) => {
    const d = newVal - baseVal;
    return (
      <div className="text-xs">
        <div className="text-[color:var(--pe-text-muted)]">{label}</div>
        <div className="font-mono-spec">
          {fmtNum(baseVal, 2)}{unit} → <span className="font-semibold">{fmtNum(newVal, 2)}{unit}</span>{" "}
          <span className={d >= 0 ? "text-emerald-600" : "text-amber-700"}>({d >= 0 ? "+" : ""}{fmtNum(d, 2)}{unit})</span>
        </div>
      </div>
    );
  };

  return (
    <div className="pe-mobile-cta-safe" data-testid="page-rencana-tumbuh">
      <div className="pe-container pt-10 pb-6">
        <div className="pe-eyebrow">Rencana Tumbuh</div>
        <h1 className="mt-2 font-display font-bold text-3xl lg:text-4xl">Konfigurasi sekarang vs skenario pengembangan</h1>
        <p className="mt-3 text-[color:var(--pe-text-muted)] max-w-2xl">
          Lihat perubahan kapasitas, baterai, dan estimasi investasi tambahan untuk tiga skenario pengembangan. Harga tahap kedua tidak dijamin sama dengan harga saat ini.
        </p>
      </div>

      <div className="pe-container grid lg:grid-cols-4 gap-4 mb-6">
        <div className="pe-card">
          <label className="text-xs text-[color:var(--pe-text-muted)]">Tier dasar</label>
          <select value={tier} onChange={(e) => setTier(e.target.value)} className="w-full mt-2 p-2 border rounded-md" data-testid="rt-select-tier">
            <option>Starter</option><option>Safer</option><option>Signature</option>
          </select>
        </div>
        <div className="pe-card">
          <label className="text-xs text-[color:var(--pe-text-muted)]">Tambah panel</label>
          <Input type="number" value={addPanels} onChange={(e) => setAddPanels(Number(e.target.value))} className="mt-2" data-testid="rt-add-panels" />
        </div>
        <div className="pe-card">
          <label className="text-xs text-[color:var(--pe-text-muted)]">Tambah modul baterai</label>
          <Input type="number" value={addBatt} onChange={(e) => setAddBatt(Number(e.target.value))} className="mt-2" data-testid="rt-add-batt" />
        </div>
        <div className="pe-card flex items-center">
          <Link to="/konfigurator" className="pe-btn-ghost w-full justify-center text-sm" data-testid="rt-back-config">Kembali ke konfigurator <ArrowRight size={14} /></Link>
        </div>
      </div>

      {current && (
        <div className="pe-container grid lg:grid-cols-4 gap-5">
          <div className="pe-card">
            <div className="pe-eyebrow">Sekarang</div>
            <h3 className="font-display font-semibold text-lg mt-2">{tier} · {current.pkg.va} VA</h3>
            <ul className="mt-3 text-sm space-y-1 font-mono-spec">
              <li>{current.pkg.panels} panel · {current.pkg.kwp} kWp</li>
              <li>Inverter {current.pkg.inverter_kw} kW</li>
              <li>Baterai {current.pkg.battery_units} × {current.pkg.battery_kwh_per_unit} kWh</li>
              <li className="pt-2 border-t mt-2">{fmtRp(current.estimate.total_rp)}</li>
            </ul>
          </div>

          <ScenarioCard
            title={`+${addPanels} panel`}
            icon={Sun}
            base={current}
            scenario={scenarios.addP}
            description="Produksi harian lebih besar; cocok bila konsumsi siang masih belum tertutup."
            testid="scenario-add-panel"
          />
          <ScenarioCard
            title={`+${addBatt} baterai`}
            icon={Battery}
            base={current}
            scenario={scenarios.addB}
            description="Lebih banyak energi untuk malam & backup. Hanya efektif bila surplus PV cukup untuk mengisi."
            testid="scenario-add-batt"
          />
          <ScenarioCard
            title={`+${addPanels} panel · +${addBatt} baterai`}
            icon={TrendingUp}
            base={current}
            scenario={scenarios.addBoth}
            description="Kombinasi — perhatikan batas inverter dan atap."
            testid="scenario-add-both"
          />
        </div>
      )}

      <div className="pe-container mt-10">
        <div className="pe-card">
          <div className="pe-eyebrow">Catatan</div>
          <ul className="mt-3 text-sm text-[color:var(--pe-text-muted)] space-y-2 list-disc pl-5">
            <li>Harga addon memakai allowance DEMO (Rp3,5 juta/panel, Rp14 juta/modul 5,12 kWh) selama katalog aktual belum lengkap.</li>
            <li>Peningkatan baterai saat produksi PV belum mencukupi tidak otomatis menambah penghematan.</li>
            <li>Batas inverter, MPPT, atap, dan desain struktural tetap menjadi penentu kelayakan pengembangan.</li>
            <li>Simulasi energi pada rencana tumbuh menyusul; lihat halaman Simulasi untuk estimasi energi kondisi awal.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function ScenarioCard({ title, icon: Icon, base, scenario, description, testid }) {
  if (!scenario) return <div className="pe-card">Memuat…</div>;
  const deltaRp = scenario.total_rp - base.estimate.total_rp;
  return (
    <div className="pe-card" data-testid={testid}>
      <div className="flex items-center gap-2"><Icon size={18} style={{ color: "var(--pe-primary)" }} /><div className="pe-eyebrow">Skenario</div></div>
      <h3 className="font-display font-semibold text-lg mt-2">{title}</h3>
      <p className="text-xs text-[color:var(--pe-text-muted)] mt-1">{description}</p>
      <ul className="mt-4 text-sm space-y-1 font-mono-spec">
        <li>Panel: {scenario.panels} · {scenario.kwp} kWp</li>
        <li>Baterai: {scenario.battery_units} unit · {scenario.battery_nominal_kwh.toFixed(2)} kWh</li>
      </ul>
      <div className="mt-3 pt-3 border-t">
        <div className="text-xs text-[color:var(--pe-text-muted)]">Investasi tambahan</div>
        <div className="font-mono-spec text-lg font-semibold" style={{ color: "var(--pe-primary)" }}>+{fmtRp(deltaRp)}</div>
      </div>
      {scenario.warnings?.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs text-amber-700">
          {scenario.warnings.map((w, i) => <li key={i}>• {w}</li>)}
        </ul>
      )}
    </div>
  );
}
